import { Card, CardContent } from "../../components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { getDevices } from "../../api/axios";
import React from "react";
import { Monitor, CheckCircle2, AlertTriangle, AlertCircle } from "lucide-react";

type StatusCardProps = {
  title: string;
  count: number;
  percentage?: number;
  icon: React.ReactNode;
  iconBgClass: string;
  iconTextClass: string;
  linkText: string;
  linkHref: string;
};

function StatusCard({
  title,
  count,
  percentage,
  icon,
  iconBgClass,
  iconTextClass,
  linkText,
  linkHref,
}: StatusCardProps) {
  return (
    <Card className="bg-white overflow-hidden shadow rounded-lg border border-gray-200">
      <CardContent className="p-0">
        <div className="p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className={`rounded-md ${iconBgClass} p-3`}>
                <span className={iconTextClass}>{icon}</span>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  {title}
                </dt>
                <dd className="flex items-baseline">
                  <div className="text-2xl font-semibold text-neutral-dark">
                    {count}
                  </div>
                  {percentage !== undefined && (
                    <div className="ml-2 flex items-baseline text-sm font-semibold text-green-600">
                      <span>{percentage}%</span>
                    </div>
                  )}
                </dd>
              </dl>
            </div>
          </div>
        </div>
        <div className="bg-gray-50 px-5 py-3">
          <div className="text-sm">
            <Link
              to={linkHref}
              className="font-medium text-primary hover:text-primary-dark"
            >
              {linkText}
            </Link>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function StatusSummary() {
  const { data: devices, isLoading } = useQuery({
    queryKey: ["/api/devices"],
    queryFn: getDevices,
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="bg-white shadow rounded-lg border border-gray-200">
            <CardContent className="p-5 h-24 flex items-center justify-center">
              <div className="w-12 h-12 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!devices || devices.length === 0) {
    return null;
  }

  const totalDevices = devices.length;
  const devicesUp = devices.filter((d: any) => d.status === "up").length;
  const devicesWarning = devices.filter((d: any) => d.status === "warning").length;
  const devicesDown = devices.filter((d: any) => d.status === "down").length;

  const upPercentage = totalDevices ? Math.round((devicesUp / totalDevices) * 100) : 0;
  const warningPercentage = totalDevices ? Math.round((devicesWarning / totalDevices) * 100) : 0;
  const downPercentage = totalDevices ? Math.round((devicesDown / totalDevices) * 100) : 0;

  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6">
      <StatusCard
        title="Total Devices"
        count={totalDevices}
        icon={<Monitor className="h-6 w-6" />}
        iconBgClass="bg-gray-100"
        iconTextClass="text-gray-600"
        linkText="View all devices"
        linkHref="/manage-devices"
      />
      <StatusCard
        title="Devices Up"
        count={devicesUp}
        percentage={upPercentage}
        icon={<CheckCircle2 className="h-6 w-6" />}
        iconBgClass="bg-secondary bg-opacity-10"
        iconTextClass="text-secondary"
        linkText="View healthy devices"
        linkHref="/manage-devices?status=up"
      />
      <StatusCard
        title="Devices with Warnings"
        count={devicesWarning}
        percentage={warningPercentage}
        icon={<AlertTriangle className="h-6 w-6" />}
        iconBgClass="bg-warning bg-opacity-10"
        iconTextClass="text-warning"
        linkText="View devices with warnings"
        linkHref="/manage-devices?status=warning"
      />
      <StatusCard
        title="Devices Down"
        count={devicesDown}
        percentage={downPercentage}
        icon={<AlertCircle className="h-6 w-6" />}
        iconBgClass="bg-danger bg-opacity-10"
        iconTextClass="text-danger"
        linkText="View down devices"
        linkHref="/manage-devices?status=down"
      />
    </div>
  );
}
